// Швидка розмітка: показуємо питання по черзі, юзер клікає правильний варіант
// → endpoint виставляє isCorrect → автоматичний перехід до наступного.
//
// Особливості UX:
//  - Завантажуємо ВСІ питання категорії (батч). На 1 категорію — звичайно <500.
//  - Стартуємо з першого непоміченого (де нема жодного isCorrect: true).
//  - Прогрес-бар по темі. Пропустити дозволяємо.
//  - Підсвічуємо вже існуючу правильну (якщо була) — при перепрогоні зрозуміло, де ти.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, ApiError, type AdminCategory } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';
import { haptic } from '../../lib/telegram';

type FullQuestion = {
  id: number;
  textUk: string;
  imageUrl: string | null;
  order: number;
  category: { nameUk: string };
  answers: Array<{ id: number; textUk: string; isCorrect: boolean; order: number }>;
};

export function AdminMarkCorrect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const categoryId = params.get('categoryId') ? Number(params.get('categoryId')) : undefined;

  const cats = useFetch<AdminCategory[]>(() => api.admin.listCategories(), []);

  // Завантажуємо ВСІ питання категорії (limit=1000 покриває майже всі теми).
  // У Дорожніх знаках 326 — лізе. У Європротоколі 595 — теж лізе.
  const list = useFetch(
    () =>
      api.admin
        .listQuestions({ categoryId, isActive: 'true', limit: 1000 })
        .then(async (page) => {
          // Підвантажуємо повні відповіді — getQuestion повертає answers
          const full = await Promise.all(
            page.items.map((q) => api.admin.getQuestion(q.id)),
          );
          return full as FullQuestion[];
        }),
    [categoryId],
  );

  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  // Локальний кеш правильних відповідей — оновлюється після markCorrect, щоб не перевантажувати весь список
  const [correctMap, setCorrectMap] = useState<Record<number, number | null>>({});
  // Локальний кеш URL картинок — щоб після зміни одразу побачити нову, без перезавантаження списку
  const [imageMap, setImageMap] = useState<Record<number, string | null>>({});
  const [imgBusy, setImgBusy] = useState(false);
  const [imgError, setImgError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // На завантаженні — стрибаємо до першого без правильної відповіді
  useEffect(() => {
    if (!list.data) return;
    const initial: Record<number, number | null> = {};
    const imgs: Record<number, string | null> = {};
    list.data.forEach((q) => {
      const correct = q.answers.find((a) => a.isCorrect);
      initial[q.id] = correct ? correct.id : null;
      imgs[q.id] = q.imageUrl;
    });
    setCorrectMap(initial);
    setImageMap(imgs);
    const firstUnmarked = list.data.findIndex((q) => !q.answers.some((a) => a.isCorrect));
    setIndex(firstUnmarked === -1 ? 0 : firstUnmarked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data]);

  const totalDone = useMemo(() => Object.values(correctMap).filter((v) => v !== null).length, [correctMap]);
  const total = list.data?.length ?? 0;
  const current = list.data?.[index];
  const currentCorrect = current ? correctMap[current.id] : null;
  const currentImage = current ? imageMap[current.id] ?? null : null;

  async function pick(answerId: number) {
    if (!current || busy) return;
    setBusy(true);
    haptic.success();
    try {
      await api.admin.markCorrect(current.id, answerId);
      setCorrectMap((prev) => ({ ...prev, [current.id]: answerId }));
      // Невелика затримка щоб юзер побачив зелену рамку
      setTimeout(() => {
        next();
        setBusy(false);
      }, 250);
    } catch {
      setBusy(false);
      haptic.error();
    }
  }

  function next() {
    if (!list.data) return;
    setIndex((i) => Math.min(list.data!.length - 1, i + 1));
  }
  function prev() {
    setIndex((i) => Math.max(0, i - 1));
  }
  function jumpToFirstUnmarked() {
    if (!list.data) return;
    const i = list.data.findIndex((q) => correctMap[q.id] == null);
    if (i !== -1) setIndex(i);
  }

  async function handleReplaceImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !current) return;
    setImgBusy(true);
    setImgError(null);
    try {
      const up = await api.admin.uploadImage(file);
      await api.admin.updateQuestion(current.id, { imageUrl: up.url });
      setImageMap((m) => ({ ...m, [current.id]: up.url }));
      haptic.success();
    } catch (err) {
      setImgError(err instanceof ApiError ? err.message : 'Не вдалося замінити картинку');
      haptic.error();
    } finally {
      setImgBusy(false);
    }
  }

  async function handleRemoveImage() {
    if (!current || !confirm('Прибрати картинку з цього питання?')) return;
    setImgBusy(true);
    setImgError(null);
    try {
      await api.admin.updateQuestion(current.id, { imageUrl: null });
      setImageMap((m) => ({ ...m, [current.id]: null }));
      haptic.light();
    } catch (err) {
      setImgError(err instanceof ApiError ? err.message : 'Не вдалося прибрати картинку');
    } finally {
      setImgBusy(false);
    }
  }

  if (cats.loading || list.loading) return <Loader label="Завантаження питань…" />;
  if (cats.error) return <ErrorView error={cats.error} onRetry={cats.reload} />;
  if (list.error) return <ErrorView error={list.error} onRetry={list.reload} />;

  if (!list.data || list.data.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="mb-4 text-tg-hint">У цій категорії немає питань.</p>
        <Link to="/admin/questions" className="btn-primary inline-block">
          ← До списку
        </Link>
      </div>
    );
  }

  if (!current) return null;

  const categoryName = current.category.nameUk;
  const progressPct = total > 0 ? (totalDone / total) * 100 : 0;

  return (
    <div className="space-y-3 p-4">
      {/* Шапка з прогресом */}
      <div className="card space-y-2">
        <div className="flex items-center justify-between text-xs text-tg-hint">
          <span>{categoryName}</span>
          <span>
            Розмічено <strong className="text-tg-text">{totalDone}</strong> / {total}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-tg-bg">
          <div
            className="h-full bg-tg-success transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-tg-hint">
          <span>
            Питання <strong className="text-tg-text">{index + 1}</strong> з {total}
            {current.order > 0 && <> · #{current.order} в темі</>}
          </span>
          <button onClick={jumpToFirstUnmarked} className="text-tg-link">
            До першого нерозміченого ↓
          </button>
        </div>
      </div>

      {/* Текст питання + картинка з можливістю заміни */}
      <article className="card">
        <p className="mb-3 text-base">
          {current.order > 0 && (
            <span className="font-semibold text-tg-link">{current.order}. </span>
          )}
          {current.textUk}
        </p>
        {currentImage ? (
          <div className="space-y-2">
            <div className="relative">
              <img
                src={currentImage}
                alt=""
                className="w-full rounded-lg bg-tg-bg object-contain"
                loading="lazy"
              />
              {imgBusy && (
                <div className="absolute inset-0 grid place-items-center rounded-lg bg-black/50 text-sm text-white">
                  Завантаження…
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={imgBusy}
                className="btn-secondary flex-1"
              >
                🖼 Замінити
              </button>
              <button
                type="button"
                onClick={handleRemoveImage}
                disabled={imgBusy}
                className="btn-secondary flex-1 text-tg-destructive"
              >
                ✕ Прибрати
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={imgBusy}
            className="grid w-full place-items-center rounded-lg border-2 border-dashed border-tg-hint bg-tg-bg p-6 text-sm text-tg-hint hover:bg-tg-bg/60"
          >
            {imgBusy ? 'Завантаження…' : '+ Додати картинку (PNG/JPG/WEBP, до 5 МБ)'}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleReplaceImage}
          className="hidden"
        />
        {imgError && (
          <p className="mt-2 text-xs text-tg-destructive">{imgError}</p>
        )}
      </article>

      {/* Варіанти — клік позначає правильну */}
      <div className="space-y-2">
        {current.answers.map((a, i) => {
          const isCorrect = currentCorrect === a.id;
          let cls = 'card flex w-full items-center gap-3 text-left transition disabled:opacity-50';
          if (isCorrect) cls += ' border-2 border-tg-success bg-tg-success/10';
          else if (currentCorrect !== null) cls += ' opacity-60';
          else cls += ' hover:bg-tg-bg/40';

          return (
            <button
              key={a.id}
              disabled={busy}
              onClick={() => pick(a.id)}
              className={cls}
            >
              <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold ${isCorrect ? 'bg-tg-success text-white' : 'bg-tg-bg'}`}>
                {isCorrect ? '✓' : String.fromCharCode(65 + i)}
              </span>
              <span className="flex-1">{a.textUk}</span>
            </button>
          );
        })}
      </div>

      {/* Навігація */}
      <div className="flex items-center gap-2 pt-2">
        <button onClick={prev} disabled={index === 0} className="btn-secondary flex-1 disabled:opacity-40">
          ← Назад
        </button>
        <button onClick={next} disabled={index >= total - 1} className="btn-secondary flex-1 disabled:opacity-40">
          Пропустити →
        </button>
      </div>

      <button
        onClick={() => navigate(-1)}
        className="btn-secondary w-full"
      >
        Завершити розмітку
      </button>
    </div>
  );
}
